import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { getAdminDashboard } from "app/services/api/management";

const USER_TYP_ADMIN = 1;

const SEVERITY_VALUES = ["critical", "warning", "info"];
const STATUS_VALUES = ["new", "inProgress", "resolved"];
const PROBLEM_TYPES = [
  "offline",
  "healthCheckNotResponding",
  "syncFailed",
  "posIntegrationError",
  "ekasaError",
];

const defaultOverview = () => ({
  activeCustomers: 0,
  activeDevices: 0,
  devicesOnline: 0,
  devicesOffline: 0,
  devicesInMaintenance: 0,
  errorsLast24h: 0,
  customersWithProblems: 0,
});

function KpiCard({ title, value, subtext, status = "ok", loading }) {
  const color =
    status === "critical"
      ? "error.main"
      : status === "warning"
        ? "warning.main"
        : "success.main";
  return (
    <Card variant="outlined" sx={{ height: "100%", minHeight: 100 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h4" sx={{ color: value !== 0 ? color : "text.primary" }}>
            {value}
          </Typography>
        )}
        {subtext && (
          <Typography variant="caption" color="text.secondary">
            {subtext}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  const userTyp = authUser?.type ?? authUser?.typ ?? authUser?.customer?.customerType;
  const isAdmin = Number(userTyp) === USER_TYP_ADMIN;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    overview: defaultOverview(),
    problems: [],
    recentOrders: [],
    recentSyncErrors: [],
  });
  const [severityFilter, setSeverityFilter] = useState("warning");
  const [statusFilter, setStatusFilter] = useState("new");
  const [problemsSortOrder, setProblemsSortOrder] = useState("desc");

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminDashboard({
      severity: severityFilter,
      status: statusFilter,
      limitProblems: 100,
      limitActivity: 100,
    })
      .then((mapped) => {
        if (cancelled) return;
        setData({
          overview: { ...defaultOverview(), ...mapped.overview },
          problems: mapped.problems ?? [],
          recentOrders: (mapped.recentOrders ?? []).slice(0, 10),
          recentSyncErrors: (mapped.recentSyncErrors ?? []).slice(0, 10),
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? "Failed to load dashboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAdmin, severityFilter, statusFilter]);

  const filteredAndSortedProblems = useMemo(() => {
    const list = [...(data.problems || [])];
    list.sort((a, b) => {
      const ta = new Date(a.lastOccurrence || a.lastOccurrenceTimestamp || 0).getTime();
      const tb = new Date(b.lastOccurrence || b.lastOccurrenceTimestamp || 0).getTime();
      return problemsSortOrder === "desc" ? tb - ta : ta - tb;
    });
    return list;
  }, [data.problems, problemsSortOrder]);

  const handleProblemRowClick = (row) => {
    const url = row.customerUrl ?? row.customer_url;
    if (url) navigate(`/dsok/customer-detail/${url}/devices`);
  };

  const handleOrderClick = (row) => {
    const url = row.customerUrl ?? row.customer_url;
    if (url) navigate(`/dsok/customer-detail/${url}`);
  };

  const handleSyncErrorClick = (row) => {
    const url = row.customerUrl ?? row.customer_url;
    if (url) navigate(`/dsok/customer-detail/${url}/devices`);
  };

  const problemTypeLabel = (type) =>
    t(`admin.dashboard.problemType.${type}`, { defaultValue: type });
  const statusLabel = (status) =>
    t(`admin.dashboard.status.${status}`, { defaultValue: status });
  const severityLabel = (s) => t(`admin.dashboard.severity.${s}`, { defaultValue: s });

  if (!isAdmin) {
    navigate("/", { replace: true });
    return null;
  }

  const o = data.overview;

  return (
    <Box sx={{ px: 0 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Typography color="text.primary">
          {t("admin.dashboard.title", { defaultValue: "Admin Dashboard" })}
        </Typography>
      </Breadcrumbs>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "error.light" }}>
          <Typography color="error.contrastText">{error}</Typography>
        </Paper>
      )}

      {/* Section A – System Overview */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("admin.dashboard.sectionOverview", { defaultValue: "System Overview" })}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          title={t("admin.dashboard.kpi.activeCustomers", { defaultValue: "Active customers" })}
          value={o.activeCustomers}
          loading={loading}
        />
        <KpiCard
          title={t("admin.dashboard.kpi.activeDevices", { defaultValue: "Active devices" })}
          value={o.activeDevices}
          loading={loading}
        />
        <KpiCard
          title={t("admin.dashboard.kpi.devicesOnlineOffline", { defaultValue: "Devices online / offline" })}
          value={`${o.devicesOnline ?? 0} / ${o.devicesOffline ?? 0}`}
          loading={loading}
        />
        <KpiCard
          title={t("admin.dashboard.kpi.devicesInMaintenance", { defaultValue: "Devices in maintenance" })}
          value={o.devicesInMaintenance ?? 0}
          status={o.devicesInMaintenance > 0 ? "warning" : "ok"}
          loading={loading}
        />
        <KpiCard
          title={t("admin.dashboard.kpi.errorsLast24h", { defaultValue: "Errors (last 24h)" })}
          value={o.errorsLast24h ?? 0}
          subtext={t("admin.dashboard.last24h", { defaultValue: "last 24h" })}
          status={o.errorsLast24h > 0 ? "critical" : "ok"}
          loading={loading}
        />
        <KpiCard
          title={t("admin.dashboard.kpi.customersWithProblems", { defaultValue: "Customers with problems" })}
          value={o.customersWithProblems ?? 0}
          status={o.customersWithProblems > 0 ? "warning" : "ok"}
          loading={loading}
        />
      </Box>

      {/* Section B – Health & Problems */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("admin.dashboard.sectionHealth", { defaultValue: "Health & Problems" })}
      </Typography>
      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("admin.dashboard.severityFilter", { defaultValue: "Severity" })}</InputLabel>
          <Select
            value={severityFilter}
            label={t("admin.dashboard.severityFilter", { defaultValue: "Severity" })}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <MenuItem value="">{t("admin.dashboard.all", { defaultValue: "All" })}</MenuItem>
            {SEVERITY_VALUES.map((s) => (
              <MenuItem key={s} value={s}>{severityLabel(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("admin.dashboard.statusFilter", { defaultValue: "Status" })}</InputLabel>
          <Select
            value={statusFilter}
            label={t("admin.dashboard.statusFilter", { defaultValue: "Status" })}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t("admin.dashboard.all", { defaultValue: "All" })}</MenuItem>
            {STATUS_VALUES.map((s) => (
              <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={200} />
          </Box>
        ) : filteredAndSortedProblems.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              {t("admin.dashboard.noActiveIssues", { defaultValue: "No active issues" })}
            </Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.dashboard.customer", { defaultValue: "Customer" })}</TableCell>
                <TableCell>{t("admin.dashboard.device", { defaultValue: "Device" })}</TableCell>
                <TableCell>{t("admin.dashboard.problemTypeColumn", { defaultValue: "Problem type" })}</TableCell>
                <TableCell sortDirection={problemsSortOrder === "desc" ? "desc" : "asc"}>
                  <TableSortLabel
                    active
                    direction={problemsSortOrder}
                    onClick={() => setProblemsSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
                  >
                    {t("admin.dashboard.lastOccurrence", { defaultValue: "Last occurrence" })}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t("admin.dashboard.statusColumn", { defaultValue: "Status" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedProblems.map((row, idx) => (
                <TableRow
                  key={row.id ?? idx}
                  hover
                  onClick={() => handleProblemRowClick(row)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.customerName ?? row.customer_name ?? "—"}</TableCell>
                  <TableCell>
                    {[row.deviceName ?? row.device_name, row.locationName ?? row.location_name]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={problemTypeLabel(row.problemType ?? row.problem_type)}
                      color={row.severity === "critical" ? "error" : row.severity === "warning" ? "warning" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {row.lastOccurrence
                      ? new Date(row.lastOccurrence).toLocaleString()
                      : row.lastOccurrenceTimestamp
                        ? new Date(row.lastOccurrenceTimestamp).toLocaleString()
                        : "—"}
                  </TableCell>
                  <TableCell>{statusLabel(row.status ?? "new")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Section C – Recent Activity */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("admin.dashboard.sectionRecentActivity", { defaultValue: "Recent Activity" })}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("admin.dashboard.recentOrders", { defaultValue: "Latest orders" })}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (data.recentOrders || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("admin.dashboard.noData", { defaultValue: "No data" })}
            </Typography>
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {data.recentOrders.map((item, i) => (
                <Box
                  component="li"
                  key={item.id ?? i}
                  onClick={() => handleOrderClick(item)}
                  sx={{ cursor: "pointer", py: 0.5, "&:hover": { color: "primary.main" } }}
                >
                  {item.orderNumber ?? item.order_number ?? "—"} · {item.customerName ?? item.customer_name ?? "—"} ·{" "}
                  {item.time ? new Date(item.time).toLocaleString() : "—"} · {item.amount ?? "—"}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("admin.dashboard.recentSyncErrors", { defaultValue: "Latest sync errors" })}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (data.recentSyncErrors || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("admin.dashboard.noData", { defaultValue: "No data" })}
            </Typography>
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {data.recentSyncErrors.map((item, i) => (
                <Box
                  component="li"
                  key={item.id ?? i}
                  onClick={() => handleSyncErrorClick(item)}
                  sx={{ cursor: "pointer", py: 0.5, "&:hover": { color: "primary.main" } }}
                >
                  {item.customerName ?? item.customer_name ?? "—"} · {item.deviceName ?? item.device_name ?? "—"} ·{" "}
                  {item.errorType ?? item.error_type ?? "—"} ·{" "}
                  {item.time ? new Date(item.time).toLocaleString() : "—"}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AdminDashboardPage;
